import * as React from "react";
import { useState, useCallback, useMemo } from "react";
import { Button, Col, Modal } from "components/bootstrap";
import ModalButtonToolbar from "components/common/ModalButtonToolbar";
import { useUploadSigmaRules } from "security-app/hooks/useSigmaAPI";
import { FlexRow, FlexCol, StyledProgressBar, StyledAlert } from "./styled-components";
import { getBodyPayload, hasError } from "./utils";
import FileList from "./FileList";
import FileOptionsForm from "./FileOptionsForm";
const Buttons = ({ confirmDisabled, cancelDisabled, onCancel, uploadFiles }) => /* @__PURE__ */ React.createElement(ModalButtonToolbar, null, /* @__PURE__ */ React.createElement(Button, { onClick: onCancel, disabled: cancelDisabled }, "Cancel"), /* @__PURE__ */ React.createElement(Button, { bsStyle: "primary", onClick: uploadFiles, disabled: confirmDisabled }, "Upload rules"));
function UploadRulesModal({ onClose }) {
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(files[0]);
  const [uploadProgress, setUploadProgress] = useState();
  const [validFormStateMap, setValidFormStateMap] = useState({});
  const { uploadSigmaRules, uploadingRules } = useUploadSigmaRules();
  const reportProgress = useCallback((percent) => {
    setUploadProgress(percent);
  }, []);
  const uploadFiles = useCallback(async () => {
    const bodyPayload = getBodyPayload(files);
    await uploadSigmaRules({
      fileForms: files,
      body: bodyPayload,
      progressCB: reportProgress
    });
    const badFiles = files.filter((fileForm) => hasError(fileForm));
    if (badFiles.length > 0) {
      setFiles(badFiles);
      setSelectedFile(void 0);
      setUploadProgress(void 0);
      return;
    }
    onClose();
  }, [onClose, files, uploadSigmaRules, reportProgress]);
  const handleValidFormState = (formState) => {
    setValidFormStateMap((prevMap) => ({
      ...prevMap,
      [selectedFile.id]: formState.isValid
    }));
  };
  const canImport = useMemo(
    () => Object.values(validFormStateMap).every((value) => value),
    [validFormStateMap]
  );
  const renderError = (file) => {
    if (file.file.type === "application/zip") {
      return file.ruleValidation.results.map(
        (validation) => {
          if (typeof validation === "string") return /* @__PURE__ */ React.createElement("p", { key: validation }, validation);
          return /* @__PURE__ */ React.createElement("div", { key: validation.file_name }, /* @__PURE__ */ React.createElement("strong", null, validation.file_name, ":"), validation.errors.map((ruleError) => /* @__PURE__ */ React.createElement("p", { key: ruleError }, ruleError)));
        }
      );
    }
    return /* @__PURE__ */ React.createElement("div", { key: file.file.name }, /* @__PURE__ */ React.createElement("strong", null, file.file.name, ":"), file.ruleValidation.errors.map((ruleError) => /* @__PURE__ */ React.createElement("p", { key: ruleError }, ruleError)));
  };
  return /* @__PURE__ */ React.createElement(Modal, { show: true, bsSize: "xlarge", onHide: onClose }, /* @__PURE__ */ React.createElement(Modal.Header, null, /* @__PURE__ */ React.createElement(Modal.Title, null, "Upload Sigma Rules")), /* @__PURE__ */ React.createElement(Modal.Body, null, /* @__PURE__ */ React.createElement(FlexRow, null, /* @__PURE__ */ React.createElement(Col, { md: 5 }, /* @__PURE__ */ React.createElement(FileList, { files, setFiles, selectedFile, setSelectedFile })), /* @__PURE__ */ React.createElement(FlexCol, { md: 7 }, selectedFile && /* @__PURE__ */ React.createElement(
    FileOptionsForm,
    {
      selectedFile,
      setSelectedFile,
      setFiles,
      formState: handleValidFormState
    }
  ), uploadProgress && /* @__PURE__ */ React.createElement(
    StyledProgressBar,
    {
      bars: [
        { value: uploadProgress, bsStyle: "info", animated: true, label: `Uploading ${uploadProgress}%` }
      ]
    }
  ))), selectedFile && hasError(selectedFile) && /* @__PURE__ */ React.createElement(StyledAlert, { bsStyle: "danger", "aria-label": "Rule format errors" }, renderError(selectedFile))), /* @__PURE__ */ React.createElement(Modal.Footer, null, /* @__PURE__ */ React.createElement(
    Buttons,
    {
      confirmDisabled: uploadingRules || !canImport || files.length < 1,
      cancelDisabled: uploadingRules,
      onCancel: onClose,
      uploadFiles
    }
  )));
}
export default UploadRulesModal;
